import environment from '../../../common/environment';
import store from '../../../store/store';
import {
  PerformIsbnSearchParams,
  PerformSearchParams,
  SearchResultDocument,
  SearchResults,
} from '../interfaces/search.interface';
import { StoreState } from '../../../store/store.interface';
import { addPredefinedFilters } from '../utils/predefined-filter.util';
import { cleanIsbn, isSearchPage, PROFESSIONAL, reverseCatalogMap } from '../../../common/utils/util';
import { getCustomISBN } from '../store/custom-isbn.selector';
import { getCustomISBNSearch } from './custom-isbn.thunk';
import { getIsbnSearchApiCall, getSearchApiCall } from '../api/search.api';
import {
  getProfessionalPdpLink,
  isMhpProfessionalProgram,
  MHP_PDP_BASE_PATH,
} from '../../../common/constants/professional-redirects';
import { onFailure, onSuccess, setIsCustomISBN, setLoading } from '../store/search.store';
import { searchFilterFacets } from '../interfaces/search-filter.interface';
import { searchParam, selectedFacetCount, selectRequest, selectSelectedCatalog } from '../store/search.selector';
import { appendQueryParamToUrl } from '../../../common/utils/analytics-util';
import { productDiscoveryQPKey, productDiscoveryQPValue } from '../../../common/constants/anayltics.constants';
import { savePdpLocalStorageObject } from '../utils/search.util';
import { searchV2FilterFacets } from '../../search-v2/thunks/perform-search-v2.thunk';

/*----- DECLARE THUNKS ------*/
export const performIsbnSearch = (params: PerformIsbnSearchParams) => async (dispatch: Function) => {
  dispatch(setLoading());

  try {
    const searchData = await getIsbnSearchApiCall(params);
    if (searchData) {
      dispatch(onSuccess(searchData));
      return searchData;
    } else {
      dispatch(onFailure());
      return {};
    }
  } catch (error) {
    console.error(error);
    store.dispatch(onFailure());
  }
};

export const performSearch =
  (params: PerformSearchParams = {}, useOldRequest = true) =>
  async (dispatch: Function, getState: Function) => {
    const request = getSearchRequest(params, getState(), useOldRequest);

    dispatch(setLoading(request));

    try {
      const searchData = await getSearchApiCall(request);

      // Request-identity guard: `setLoading(request)` stored `request` by reference in
      // `state.searchState.search.request`. If any newer `performSearch` dispatch ran
      // while we were awaiting, that reference has been replaced. In that case our
      // response is stale and MUST NOT touch Redux (data, isLoading, isFailure) or
      // trigger side effects (redirect, custom-isbn lookup) — the newer dispatch owns
      // the user-visible state. Return undefined so callers can also short-circuit.
      if (selectRequest(getState()) !== request) {
        return undefined;
      }

      const url = handleSearchRedirection(searchData);
      if (url) {
        savePdpLocalStorageObject({
          searchComponentQuery: false,
          searchTerm: params.searchTerms,
          programSourceId: searchData?.hits[0]?.document?.id,
        });
        window.location.href = appendQueryParamToUrl(
          environment().mheducationUrl + url,
          productDiscoveryQPKey,
          productDiscoveryQPValue
        );
        return;
      } else if (isSearchPage() && searchData?.total === 0 && checkValidISBN(request?.searchTerms)) {
        await store.dispatch(getCustomISBNSearch(request?.searchTerms));
        const customISBN = getCustomISBN(getState());
        if (request?.searchTerms && customISBN?.isbn?.includes(request?.searchTerms)) {
          store.dispatch(setIsCustomISBN(getCustomISBN(getState())));
        }
      }
      if (searchData) {
        dispatch(onSuccess(searchData));
        return searchData;
      } else {
        dispatch(onFailure());
        return {};
      }
    } catch (error) {
      // Same identity guard on failure — a late-arriving rejection from a superseded
      // request must not clear the loading flag of the currently in-flight request.
      if (selectRequest(getState()) === request) {
        store.dispatch(onFailure());
      }
    }
  };

export const performSearchSpecificIsbn = async (params: PerformSearchParams = {}) => {
  const request = getSearchRequest(params, store.getState());
  store.dispatch(setLoading(request));

  try {
    const results = await getSearchApiCall(request);

    return {
      programSourceId: getProgramSourceId(results.hits[0].document),
      urlToGo: getSearchRedirectURL(results, true),
    };
  } catch (error) {
    console.log(`Failed to get the specific ISBN ${error.message}`);
    store.dispatch(onFailure());
  }

  return undefined;
};

export function getSearchRequest(params, state, useOldRequest = true): PerformSearchParams {
  let oldRequest: PerformSearchParams;
  let request: PerformSearchParams;
  if (useOldRequest) {
    oldRequest = { ...selectRequest(state) };
    if (!oldRequest) {
      oldRequest = {
        hits: true,
        searchTerms: '',
        catalog: 'UNIFIED',
        pageLength: 10,
        start: 0,
        facets: true,
      };
    }

    const selectedCatalog = selectSelectedCatalog(state);
    if (selectedCatalog) {
      params.catalog = params?.isCatalogSet ? params.catalog : selectedCatalog;
      oldRequest.catalog = selectedCatalog;
    }

    if (params.clearFilter) {
      oldRequest.filter = undefined;
    }

    if (params.clearSort) {
      oldRequest.sortBy = undefined;
    }

    if (params.filter) {
      oldRequest.filter = params.filter;
    }

    if (params.sortBy) {
      oldRequest.sortBy = params.sortBy;
    }

    oldRequest.addFacetName = undefined;
    oldRequest.addFacetValue = undefined;
    oldRequest.removeFacetName = undefined;
    oldRequest.removeFacetValue = undefined;
    request = { ...JSON.parse(JSON.stringify(oldRequest)), ...params };
  } else {
    request = { ...params };
  }

  if (params.isPlpSearch) {
    request.isPlpSearch = params.isPlpSearch;
  }

  if (!request.fieldValueFilter) {
    request.fieldValueFilter = {};
  }

  if (params.addFacetName && !request.fieldValueFilter[params.addFacetName]) {
    request.fieldValueFilter[params.addFacetName] = [];
  }

  if (params.addFacetName && params.addFacetValue) {
    request.fieldValueFilter[params.addFacetName] = request.fieldValueFilter[params.addFacetName].filter((facet) => {
      if (params.addFacetValue) {
        return !params.addFacetValue.startsWith(facet) && !facet.startsWith(params.addFacetValue);
      }
      return true;
    });

    request.fieldValueFilter[params.addFacetName].push(params.addFacetValue);
  }

  if (params.removeFacetName && !request.fieldValueFilter[params.removeFacetName]) {
    request.fieldValueFilter[params.removeFacetName] = [];
  }

  if (params.removeFacetName && params.removeFacetValue) {
    request.fieldValueFilter[params.removeFacetName] = request.fieldValueFilter[params.removeFacetName].filter(
      (value: string) => value != params.removeFacetValue
    );

    if (request.fieldValueFilter[params.removeFacetName].length === 0) {
      delete request.fieldValueFilter[params.removeFacetName];
    }
  }

  const selectedCatalog = selectSelectedCatalog(state);
  if (selectSelectedCatalog(state)) {
    let page = request.start;
    if (page && page > 1) {
      const pageNo = (page - 1) / request.pageLength! + 1;
      if (Number.isInteger(pageNo)) {
        page = pageNo;
      }
    }
    updateSearchUrl({
      searchQuery: request.searchTerms,
      catalog: reverseCatalogMap(selectedCatalog || ''),
      page: page,
      sortby: request.sortBy,
      fieldValueFilter: request.fieldValueFilter,
    });
  }
  if (!request.pageLength) {
    request.pageLength = 10;
  }
  if (params.isbnList) {
    request.isbnList = params.isbnList;
  }
  if (params.programIdList) {
    request.programIdList = params.programIdList;
  }
  if (params.requireProgramData) {
    request.requireProgramData = params.requireProgramData;
  }

  return addPredefinedFilters(request, params.predefinedFilters);
}

/**
 * adding/modifying a query search string of page url without reloading the page
 * @param param
 */
export function updateSearchUrl({
  searchQuery = '',
  page = 1,
  sortby = 'relevance',
  catalog = 'all',
  fieldValueFilter = {},
}) {
  const url = new URL(window.location.href);
  url.searchParams.set(PARAM_CATALOG, catalog.toLocaleLowerCase());
  url.searchParams.set(PARAM_SEARCH_QUERY, searchQuery);
  url.searchParams.set(PARAM_PAGE, page.toString());
  url.searchParams.set(PARAM_SORT_BY, sortby);

  const filterParamsUrl = buildUrlWithFilterParams(url, fieldValueFilter);

  const updatedUrl = removeQueryStringFromUrl(filterParamsUrl, PARAM_DISCIPLINE);
  window.history.pushState({ path: updatedUrl }, '', updatedUrl);
}

/**
 * set search query params for multiple search filters
 */
export function buildUrlWithFilterParams(url: URL, fieldValueFilter = {}) {
  searchFilterFacets.forEach((facet) => {
    url.searchParams.delete(facet);
  });

  searchV2FilterFacets.forEach((facet) => {
    url.searchParams.delete(facet);
  });

  if (Object.keys(fieldValueFilter).length !== 0) {
    for (const field in fieldValueFilter) {
      // eslint-disable-next-line no-prototype-builtins
      if (fieldValueFilter.hasOwnProperty(field)) {
        url.searchParams.set(field, fieldValueFilter[field]);
      }
    }
  }

  return url;
}

/**
 * remove query string from search query param
 * @param url
 * @param parameter
 * @returns updated url
 */
export function removeQueryStringFromUrl(url, parameter) {
  if (url.toString().includes(parameter)) {
    const newUrl = new URL(url);
    const searchParams = newUrl.searchParams;
    searchParams.delete(parameter);
    newUrl.search = searchParams.toString();
    return newUrl.toString();
  }
  return decodeURIComponent(url.toString());
}

/**
 * This function is return the reuqest object which is created from query search parameter
 * @returns value of search param
 */
export function getSearchRequestFromUrl(pageSize = 10, searchUrl = '') {
  const urlParams = new URLSearchParams(searchUrl || location.search);
  const searchQuery = urlParams.get(PARAM_SEARCH_QUERY);
  const sortby = urlParams.get(PARAM_SORT_BY);
  const catalog = urlParams.get(PARAM_CATALOG);
  let page = urlParams.get(PARAM_PAGE) || '1';
  if (page && page !== '1') {
    page = ((JSON.parse(page) - 1) * pageSize + 1).toString();
  }

  const discipline = urlParams.get(PARAM_DISCIPLINE);
  let fieldValueFilter = {};
  if (discipline) {
    fieldValueFilter = { [DISCIPLINE_PRODUCT_TAG_PATH]: [discipline] };
  }

  searchFilterFacets.forEach((facet) => {
    const fieldValues = urlParams.get(facet);
    if (fieldValues) {
      fieldValueFilter[facet] = fieldValues.split(',');
    }
  });

  const searchObj = {
    searchQuery: searchQuery!,
    page: JSON.parse(page) || 1,
    sortby: sortby!,
    catalog: catalog!,
    fieldValueFilter: fieldValueFilter!,
  };
  return searchObj;
}
/**
 * This function will be used when we have loadResults as true on the component.
 * So, if that's true and the page is not the search page, like PLP,
 * we don't want to do a redirect to the specific ISBNs
 * @param results
 * @returns
 */
function handleSearchRedirection(results: SearchResults) {
  let url = '';
  if (isSearchPage()) {
    const request = selectRequest(store.getState() as StoreState) ?? undefined;
    const validISBN = checkValidISBN(request?.searchTerms);
    url = getSearchRedirectURL(results, validISBN);
    if (url) {
      const searchObj = getSearchRequestFromUrl(10);
      searchObj.searchQuery = '';
      updateSearchUrl(searchObj);
    }
  }

  return url;
}

/**
 * In this function user will be automatically redirected to the Higher Ed or Prek-12 PDP page
 * @param results - The results from search service
 * @params isValidIsbn - if the search term is a valid isbn
 * @returns the url to redirect for the PDP if some of the conditions match
 */
function getSearchRedirectURL(results: SearchResults, isValidIsbn?: boolean) {
  if (!results.hits || !results.hits[0]) {
    return '';
  }

  let url = '';
  const isFilterApplied = selectedFacetCount(store.getState() as StoreState) > 0;
  const selectResultDoc = results.hits[0].document;
  if (
    !isFilterApplied &&
    results &&
    (results?.total === 1 || (results.total === 2 && (isValidIsbn || searchTermMatchMhid(selectResultDoc))))
  ) {
    url = selectResultDoc.path;

    if (results.total === 2) {
      return redirectSharedIsbn(results);
    }

    if (
      (selectResultDoc.type === 'HEDocument' || selectResultDoc.type === 'UnifiedDocument') &&
      (selectResultDoc.businessUnit === 'HigherEd' ||
        isMhpProfessionalProgram(selectResultDoc?.businessUnit ?? '', selectResultDoc?.id))
    ) {
      return heRedirectUrl(selectResultDoc, isValidIsbn);
    } else if (
      (selectResultDoc.type === 'SEGProgramDocument' || selectResultDoc.type === 'UnifiedDocument') &&
      selectResultDoc.documentType == 'Products'
    ) {
      return '/prek-12/product/' + getIdentifierToUseOnPrek12PDP(selectResultDoc) + '.html';
    } else if (selectResultDoc?.businessUnit == 'Professional') {
      const searchTerm = searchParam(store.getState() as StoreState);
      let queryParam = '';
      if (checkValidISBN(searchTerm)) {
        queryParam = `?isbn=${searchTerm}`;
      }
      return (
        getProfessionalPdpLink(
          selectResultDoc.mhid ?? selectResultDoc.id,
          selectResultDoc.optimizedUniqueTitle.toLowerCase()
        ) + queryParam
      );
    }
  }
  return url;
}

function getProgramSourceId(selectResultDoc: SearchResultDocument) {
  return selectResultDoc.id ?? '';
}

function searchTermMatchMhid(selectResultDoc) {
  const searchTerm = searchParam(store.getState() as StoreState);
  return searchTerm === (selectResultDoc.mhid || selectResultDoc.isbn);
}

function redirectSharedIsbn(results: SearchResults) {
  const isSharedIsbn = results.hits.find((hit) => hit.document.documentType === 'HigherEd');

  if (isSharedIsbn) {
    return '';
  }

  return segRedirect(results);
}

function segRedirect(results: SearchResults) {
  const segProduct = results.hits.find((hit) => hit.document.documentType === 'Products');

  if (segProduct) {
    return '/prek-12/product/' + getIdentifierToUseOnPrek12PDP(segProduct.document) + '.html';
  }
  return '';
}

function getIdentifierToUseOnPrek12PDP(document) {
  if (checkValidISBN(document.isbn13)) {
    return cleanIsbn(document.isbn13);
  }

  if (checkValidISBN(document.isbn)) {
    return cleanIsbn(document.isbn);
  }

  return cleanIsbn(document.mhid || document.id);
}

/**
 * return url for HE PDP
 */
function heRedirectUrl(selectResultDoc, isValidIsbn) {
  const pdpBasePath =
    selectResultDoc.businessUnit?.toLowerCase() === PROFESSIONAL.toLowerCase()
      ? MHP_PDP_BASE_PATH
      : '/highered/product/';
  const searchTerm = selectRequest(store.getState() as StoreState)?.searchTerms;
  const isHeIsbn = checkValidHeIsbn(searchTerm);
  let url = !(isValidIsbn && isHeIsbn) ? '' : pdpBasePath + cleanIsbn(searchTerm) + '.html?exactIsbn=true';
  if (isValidIsbn && isHeIsbn && selectResultDoc.optimizedUniqueTitle) {
    url = pdpBasePath + selectResultDoc.optimizedUniqueTitle.toLowerCase() + '.html?isbn=' + cleanIsbn(searchTerm);
  } else if (
    isValidIsbn &&
    selectResultDoc.IS_MASTER &&
    (searchTerm === (selectResultDoc.mhid ?? selectResultDoc.mhid) ||
      searchTerm === selectResultDoc.isbn ||
      searchTerm === selectResultDoc.isbn13)
  ) {
    if (isMhpProfessionalProgram(selectResultDoc?.businessUnit ?? '', selectResultDoc?.id)) {
      url = pdpBasePath + selectResultDoc.optimizedUniqueTitle.toLowerCase() + '.html';
    } else if (selectResultDoc.seoTitle) {
      url = pdpBasePath + selectResultDoc.seoTitle + '/' + 'M' + selectResultDoc.isbn13 + '.html';
    }
  }
  return url;
}

/**
 *  In this function validates whether a string is HigherEd ISBN or not
 * // Example usage:
    const input1 = "m1234567890123";  // "m" + 13 digits
    const input2 = "1234567890123";   // 13 digits
    const input3 = "1234567890";      // 10 digits
    const input4 = "123456789012x";   // 12 digits + 'x'
    const input5 = "123456789x";      // 9 digits + 'x'
 */
export function checkValidHeIsbn(searchTerm: string | undefined): boolean {
  const heIsbnRegex = /\b(m?\d{13}|\d{10}|\d{12}x|\d{9}x)\b/i;
  return !!searchTerm && heIsbnRegex.test(cleanIsbn(searchTerm) || '');
}

/**
 *  In this function validates whether a string is an ISBN or not
 */
export function checkValidISBN(searchQuery?: string): boolean {
  return !!searchQuery && /^(?:\d{9}[\dXx]|\d{13})$/.test(cleanIsbn(searchQuery) || '');
}

export const PARAM_CATALOG = 'bu';
export const PARAM_SEARCH_QUERY = 'searchQuery';
export const PARAM_PAGE = 'page';
export const PARAM_SORT_BY = 'sortby';
export const PARAM_DISCIPLINE = 'disciplines';
export const DISCIPLINE_PRODUCT_TAG_PATH = 'PRODUCT_TAG_PATH';
export const PARAM_ISBN = 'isbn';
